import { SelectOption } from "../../../../../../types/select";
import { omitBookingFormUiFields } from "./omitBookingFormUiFields";
import { shapeSignatoryUser } from "./shapeSignatoryUser";
import { transformKycFields } from "./transformKycFields";

type AnyObject = Record<string, unknown>;

type PrepareFormDataOptions<T extends AnyObject> = {
  rawData: T;
  usageType: string;
  isBooking: boolean;
  unitCode: string;
  kycDetails: unknown[];
};

export const prepareFormData = <T extends AnyObject>(options: PrepareFormDataOptions<T>): AnyObject => {
  const { rawData, usageType, isBooking, unitCode, kycDetails } = options;

  const { tenantType, ...restFormData } = rawData;
  let formData: AnyObject = { ...restFormData };

  if (kycDetails.length > 0) {
    formData = transformKycFields(formData);
  }

  if (usageType === "commercial") {
    const { arabicName, commercial, isSignatory, companyTenants, ...rest } = formData;
    const commercialFormData = (commercial ?? {}) as AnyObject;

    if (tenantType === "company") {
      const companyFormData = (commercialFormData.company ?? {}) as AnyObject;
      const companyName = companyFormData.name as SelectOption | string | undefined;
      const companyTenantId = (companyName as SelectOption)?.value;
      const { signatoryUser: rawSignatoryUser, ...restWithoutSignatory } = rest;
      const signatoryUser = shapeSignatoryUser(rawSignatoryUser);

      formData = {
        ...restWithoutSignatory,
        isSignatory: "no",
        tenantIdentityType: tenantType,
        email: companyFormData.email,
        name: (companyName as SelectOption)?.label || companyName,
        ...(companyTenantId ? { companyTenantId } : {}),
        companyPhone: companyFormData.phone,
        workPhone: companyFormData.workPhone,
        address: companyFormData.address,
        city: companyFormData.city,
        country: (companyFormData.country as SelectOption)?.value,
        poBox: companyFormData.poBox,
        tradeLicense: companyFormData.licenseNo,
        ...(signatoryUser ? { signatoryUser } : {}),
      };
    } else if (tenantType === "individual") {
      const individualFormData = (commercialFormData.individual ?? {}) as AnyObject;
      const { signatoryUser: rawSignatoryUser, ...restWithoutSignatory } = rest;
      const signatoryUser =
        isSignatory === "no" ? shapeSignatoryUser(rawSignatoryUser) : isSignatory === "yes" ? {} : undefined;

      formData = {
        ...restWithoutSignatory,
        isSignatory,
        tenantIdentityType: tenantType,
        email: individualFormData.email,
        name: individualFormData.name,
        mobile: individualFormData.mobile,
        designation: individualFormData.designation,
        company: individualFormData.company,
        ...(signatoryUser !== undefined ? { signatoryUser } : {}),
      };
    }
  } else if (usageType === "residential") {
    // Combine firstName and lastName into name for API compatibility
    const firstName = (formData.firstName as string) || "";
    const lastName = (formData.lastName as string) || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ") || (formData.name as string) || "";
    
    formData = {
      ...formData,
      name: fullName,
      nationality: (formData.nationality as SelectOption)?.value,
      maritalStatus: (formData.maritalStatus as SelectOption)?.value,
      countryCode: formData.countryCode, // Ensure countryCode is included in API payload
      workPhone: formData.workPhone, // Ensure workPhone is included in API payload
    };
  }

  if (!isBooking) {
    formData = { ...formData, unitCode };
  }

  return omitBookingFormUiFields(formData);
};
